import { BidDecisionGroup, Customer, Shift } from "@/graphql/__generated__/graphql";
import { NewSiteComponent, RevenueCalculationTypeComponent } from "../../Components";
import { ServiceDefaultValues, ServiceSchema, ShiftDefaultValues } from "../../Models";
import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import type { TServiceSchema, TShiftSchema } from "../../Models";
import { appendOpOrgIdParams, cleanUpRevenueComponentDataForSubmission, convertDecimalToBp } from "@/utils/helpers";
import { isEqual, merge } from "lodash";
import { Dispatch, JSX, SetStateAction, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ShiftsComponent from "../../Shifts/ShiftsComponent";
import { Button } from "@/components/ui/button";
import { CustomToastComponent, FormattedNumberInputField } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { TSiteSchema } from "../../Models/Site";
import { appendSiteAddress } from "@/utils/helpers";
import { useAddPermanentServiceMutationHook } from "@/hooks";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { CustomCombobox } from "@/components/shared/form/CustomCombobox";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Textarea } from "@/components/ui/textarea";
import { SubmitAction } from "@/constants/enums";
import { cn } from "@/lib/utils";
import useUserStore from "@/store/shield";
import { prefillPermanentServiceWithMockData } from "./mocks";
import { TransferredEmployeePercentageTooltip } from "@/components/shared";

interface Props {
  customer: Customer;
  estimateMargin: number | undefined | null;
  biddingOperationalOrganizations: Partial<BidDecisionGroup>[];
  setIsLoading: Dispatch<SetStateAction<boolean>>;
};

/**
 * AddPermanentService form component
 *
 * @param {Props} props
 *
 * @returns {JSX.Element}
 */
const AddPermanentService = (props: Props): JSX.Element => {

  // Props
  const { customer, estimateMargin, biddingOperationalOrganizations, setIsLoading } = props;

  // Hooks
  const navigate = useNavigate();

  const { t } = useTranslation(["translation", "components", "modals"]);
  const { tenderID: currentTenderID } = useParams();
  const [searchParams] = useSearchParams();
  const currentOperationalOrganizationID = searchParams.get("operationalOrganizationId");

  const onInvalid = (errors: unknown) => console.error(errors);

  const {
    control, register, watch, reset, handleSubmit, setError,
    setValue, getValues, trigger, formState: { errors }
  } = useForm<TServiceSchema>({
    resolver: zodResolver(ServiceSchema) as Resolver<TServiceSchema>,
    defaultValues: {
      ...ServiceDefaultValues,
      margin: estimateMargin ? estimateMargin / 100 : undefined,
      operationalOrganizationId: currentOperationalOrganizationID ? parseInt(currentOperationalOrganizationID) : undefined,
    },
    mode: "onChange"
  });

  // States
  const [newSite, setNewSite] = useState<boolean>(false);
  const [shiftsToDisplay, setShiftsToDisplay] = useState<Shift[] | []>([]);
  const [showRemoveShiftAlert, setShowRemoveShiftAlert] = useState<boolean>(false);

  const revenueCalculationType = watch("revenueCalculationType");

  // Mutations
  const { addPermanentServiceMutation, loading: addPermanentServiceLoading } = useAddPermanentServiceMutationHook();

  /** Prefill mock data for testing */
  const handlePrefillMockData = () => {
    const currentOrgId = useUserStore.getState().orgId;
    prefillPermanentServiceWithMockData({ setValue, trigger }, customer, biddingOperationalOrganizations, currentOrgId);
  };

  // Add event listener for prefill button
  useEffect(() => {
    const formElement = document.getElementById("add-permanent-service-form");
    if (formElement) {
      const handlePrefillEvent = () => {
        handlePrefillMockData();
      };

      formElement.addEventListener("prefillMockData", handlePrefillEvent);

      return () => {
        formElement.removeEventListener("prefillMockData", handlePrefillEvent);
      };
    }
  }, [biddingOperationalOrganizations, customer]);

  /**
   * OnSubmit handler for the addServiceMutation
   * @param {TServiceSchema} data
   * @returns
   */
  const onSubmit: SubmitHandler<TServiceSchema> = (data: TServiceSchema): void => {
    const { siteId, siteInput } = data;

    // Check for missing site
    if ((!siteId || siteId === 0) && !siteInput) {
      // Manually set errors for siteId and siteInput
      setError("siteId", {
        type: "manual",
        message: t("components:newsite.errors.siteid")
      });
      setError("siteInput", {
        type: "manual",
        message: t("components:newsite.errors.siteinput")
      });
      return;
    } else if (siteId && siteId !== 0) {
      // If siteId is present, remove siteInput
      delete data.siteInput;
    } else if (!siteId && siteInput) {
      // If siteInput is present, remove siteId
      delete data.siteId;
    }

    // Clean up date for submission based on revenue calculation type
    cleanUpRevenueComponentDataForSubmission(data, revenueCalculationType);

    try {
      addPermanentServiceMutation({
        variables: {
          tenderId: Number(currentTenderID),
          input: {
            ...data,
            nonTariffAllowance: data.nonTariffAllowance ?? 0,
            shifts: data.shifts as Shift[],
            transferredEmployeePercentage: convertDecimalToBp(data.transferredEmployeePercentage)
          },
        }
      }).then((res) => {
        // Only proceed if there are no errors
        if (!res.errors) {
          // Reset form after submit success
          reset(ServiceDefaultValues);
          // Redirect to the services tab
          navigate(`/tender/${currentTenderID}/services/all${appendOpOrgIdParams(currentOperationalOrganizationID)}`);

        }
      });
    } catch (err) {
      console.error("Error occurred:", err);
    }
  };

  /**
   * Handler function to add a new shift to the form state
   * @param {TShiftSchema} shiftData
   */
  const handleAddShift = async (shiftData: TShiftSchema, action: SubmitAction, setShowShiftsModal: Dispatch<SetStateAction<boolean>>) => {
    const currentShifts = getValues("shifts") ?? [];
    // We merge the objects to add the fields that were not edited
    const newShiftObject = merge({}, ShiftDefaultValues, shiftData);
    setValue("shifts", [...currentShifts, newShiftObject]);

    trigger("shifts");

    toast(CustomToastComponent, {
      toastId: "permanent-service-shift-added-success",
      data: {
        title: t("modals:shift.toasts.addedsuccessfully"),
        type: "success",
      }
    });

    if (action === SubmitAction.AddAndClose) {
      setShowShiftsModal(false);
    }
  };

  /**
   * Handler function to add a new site to the form state
   * @param {TSiteSchema} siteData
   */
  const handleAddSite = (siteData: TSiteSchema) => {
    setValue("siteInput", siteData);
  };

  /**
   * Handler function to remove a site from the form state.
   * Resets the siteId to 0 and sets newSite to false.
   * @returns {void}
   */
  const handleRemoveSite = (): void => {
    setValue("siteId", 0);
    setValue("siteInput", undefined);
    setNewSite(false);
  };

  /**
   * Handler function to remove a shift from the form state.
   */
  const handleRemoveShift = async (shift: Shift) => {
    // Get the current list of shifts from the form state
    const currentShifts = getValues!("shifts");

    // Filter out the shift that needs to be removed by deep comparing the objects.
    const updatedShifts = currentShifts.filter(
      (s: TShiftSchema) => !isEqual(s, shift)
    );

    // Update the form state with the new list of shifts
    setValue!("shifts", updatedShifts);

    // Close the alert dialog
    setShowRemoveShiftAlert(false);
  };

  /**
   * Handler function to edit a shift
   * @param {TShiftSchema} shiftData - The edited shift data
   */
  const handleEditShift = async (shiftData: TShiftSchema, selectedShift: Shift, setShowEditShiftModal: Dispatch<SetStateAction<boolean>>) => {
    const currentShifts = getValues("shifts") ?? [];
    // We merge the objects to add the fields that were not edited
    const newShiftObject = merge({}, ShiftDefaultValues, shiftData);

    // Find and replace the existing shift with the edited one
    const updatedShifts = currentShifts.map(shift =>
      isEqual(shift, selectedShift) ? newShiftObject : shift
    );

    setValue("shifts", updatedShifts);
    trigger("shifts");

    setShowEditShiftModal(false);
  };

  /**
   * Keeps the array of Shifts in sync with the form
   */
  useEffect(() => {
    setShiftsToDisplay(getValues("shifts") as Shift[] ?? []);
  }, [watch("shifts")]);

  // Auto-assign operational organization if there's only one option
  useEffect(() => {
    if (biddingOperationalOrganizations.length === 1) {
      const singleOrg = biddingOperationalOrganizations[0];
      if (singleOrg.operationalOrganizationId) {
        setValue("operationalOrganizationId", Number(singleOrg.operationalOrganizationId));
        trigger("operationalOrganizationId");
      }
    }
  }, [biddingOperationalOrganizations, setValue, trigger]);

  useEffect(() => {
    setIsLoading(addPermanentServiceLoading);
  }, [addPermanentServiceLoading]);

  return (
    <>
      {/* Service form */}
      <form className="space-y-6" id="add-permanent-service-form" onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <div className="flex-col-4">
          <div className="flex-wrap items-start gap-4 flex justify-between">
            {/* Name */}
            <div className="responsive-col">
              <Label htmlFor="name" className={cn(errors.name && "text-red-500")}>
                {t("permanentservice.inputs.name.label")}
              </Label>
              <Input {...register("name")} type="text" autoFocus placeholder={t("permanentservice.inputs.name.placeholder")} />
              {errors.name && (
                <p className="input-error-message">
                  {errors.name.message as string}
                </p>
              )}
            </div>

            {/* Operational Organization */}
            <div className="responsive-col">
              <Label htmlFor="operationalorganization" className={cn(errors.name && "text-red-500")}>
                {t("permanentservice.inputs.operationalorganization.label")}
              </Label>
              <CustomCombobox
                options={biddingOperationalOrganizations.map((group) => ({
                  label: group.operationalOrganizationName,
                  value: Number(group.operationalOrganizationId)
                }))}
                value={watch("operationalOrganizationId")}
                onChange={(value) => {
                  setValue("operationalOrganizationId", Number(value));
                  trigger("operationalOrganizationId");
                }}
                placeholder={t("permanentservice.inputs.operationalorganization.selectplaceholder")}
                error={!!errors.operationalOrganizationId}
              />
              {errors.operationalOrganizationId && (
                <p className="input-error-message">
                  {errors.operationalOrganizationId.message as string}
                </p>
              )}
            </div>

            {/* Hourly Wage */}
            <div className="responsive-col">
              <Label htmlFor="hourlyWage" className={cn(errors.hourlyWage && "text-red-500")}>
                {t("permanentservice.inputs.hourlywage.label")}
              </Label>
              <FormattedNumberInputField
                name="hourlyWage"
                control={control}
                additionalSuffix="/h"
                style="currency"
                placeholder={t("permanentservice.inputs.hourlywage.placeholder")}
              />
              {errors.hourlyWage && (
                <p className="input-error-message">
                  {errors.hourlyWage.message as string}
                </p>
              )}
            </div>

            {/* Non-Tariff Allowance */}
            <div className="responsive-col">
              <Label htmlFor="nonTariffAllowance" className={cn(errors.nonTariffAllowance && "text-red-500")}>
                {t("permanentservice.inputs.nontariffallowance.label")}
              </Label>
              <FormattedNumberInputField
                name="nonTariffAllowance"
                control={control}
                additionalSuffix="/h"
                style="currency"
                placeholder={t("permanentservice.inputs.nontariffallowance.placeholder")}
              />
              {errors.nonTariffAllowance && (
                <p className="input-error-message">
                  {errors.nonTariffAllowance.message as string}
                </p>
              )}
            </div>

            {/* Hours Briefing per Employee */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="hoursBriefingPerEmployee" className={cn(errors.hoursBriefingPerEmployee && "text-red-500")}>
                {t("permanentservice.inputs.hoursbriefingperemployee.label")}
              </Label>
              <FormattedNumberInputField
                name="hoursBriefingPerEmployee"
                control={control}
                additionalSuffix={t("permanentservice.inputs.hoursbriefingperemployee.suffix")}
                placeholder={t("permanentservice.inputs.hoursbriefingperemployee.placeholder")}
                spaceBetweenSuffix={true}
                isInteger
              />
              {errors.hoursBriefingPerEmployee && (
                <p className="input-error-message">
                  {errors.hoursBriefingPerEmployee.message as string}
                </p>
              )}
            </div>

            {/* Transferred Employee Percentage */}
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-0.5">
                <Label htmlFor="transferredEmployeePercentage" className={cn("flex items-start", errors.transferredEmployeePercentage && "text-red-500")}>
                  {t("permanentservice.inputs.transferredemployeepercentage.label")}
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 p-0 hover:cursor-help ml-1 items-start text-orange-500"
                        type="button"
                      >
                        <InformationCircleIcon className="h-5 w-5" />
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-fit">
                      <TransferredEmployeePercentageTooltip />
                    </HoverCardContent>
                  </HoverCard>
                </Label>
              </div>
              <FormattedNumberInputField
                name="transferredEmployeePercentage"
                control={control}
                additionalSuffix="%"
                style="decimal"
                placeholder={t("permanentservice.inputs.transferredemployeepercentage.placeholder")}
                className="max-w-[200px]"
              />
              {errors.transferredEmployeePercentage && (
                <p className="input-error-message">
                  {errors.transferredEmployeePercentage.message as string}
                </p>
              )}
            </div>

            {/* Switches - Pause Included & Additional Service */}
            <div className="flex flex-1 gap-4">
              {/* Pause Included */}
              <div className="flex flex-col gap-2">
                <Label htmlFor="pauseIncluded" className="flex items-start">
                  {t("permanentservice.inputs.pauseincluded.label")}
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 p-0 hover:cursor-help ml-1 items-start"
                        type="button"
                      >
                        <InformationCircleIcon className="h-5 w-5" />
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent>
                      <p className="max-w-[320px]">{t("permanentservice.inputs.pauseincluded.note")}</p>
                    </HoverCardContent>
                  </HoverCard>
                </Label>
                <Switch
                  checked={watch("pauseIncluded")}
                  {...register("pauseIncluded")}
                  onCheckedChange={(checked) => {
                    setValue("pauseIncluded", checked);
                  }}
                />
              </div>

              {/* Additional */}
              <div className="flex flex-col gap-2">
                <Label htmlFor="additional" className="flex items-start">
                  {t("permanentservice.inputs.additional.label")}
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 p-0 hover:cursor-help ml-1 items-start"
                        type="button"
                      >
                        <InformationCircleIcon className="h-5 w-5" />
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent>
                      <p className="max-w-[320px]">
                        {t("permanentservice.inputs.additional.note")}
                      </p>
                    </HoverCardContent>
                  </HoverCard>
                </Label>
                <Switch
                  checked={watch("additional")}
                  {...register("additional")}
                  onCheckedChange={(checked) => {
                    setValue("additional", checked);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="info" className={cn(errors.info && "text-red-500")}>
              {t("permanentservice.inputs.info.label")}
            </Label>
            <Textarea
              {...register("info")}
              placeholder={t("permanentservice.inputs.info.placeholder")}
              className={cn("max-h-[200px]", errors.info && "border-red-500")}
            />
            {errors.info && (
              <p className="input-error-message">
                {errors.info.message as string}
              </p>
            )}
          </div>

          {/* Revenue Calculation Type */}
          <RevenueCalculationTypeComponent
            control={control}
            errors={errors}
            setValue={setValue}
            register={register}
            watch={watch}
            trigger={trigger}
            defaultValues={{ margin: estimateMargin ? estimateMargin / 100 : undefined }}
          />

          {/* <div className="flex flex-col md:flex-row gap-4 flex-1">
            Pay Group
            <div className="w-full md:max-w-[49%] md:flex-1 flex flex-col gap-2">
                              <Label htmlFor="payGroup" className={cn(errors.payGroup && "text-red-500")}>
                  {t("permanentservice.inputs.paygroup.label")}
                </Label>
              <Select onValueChange={(value) => {setValue("payGroup", value as "Group 1" | "Group 2" | "Group 3"); trigger("payGroup");}} >
                <SelectTrigger id="payGroup">
                  <SelectValue placeholder={t("permanentservice.inputs.paygroup.selectplaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {["Group 1", "Group 2", "Group 3"].map((group) => (
                    <SelectItem key={group} value={group}>
                      {toNormalCase(group)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.payGroup && (
                <p className="input-error-message">
                  {errors.payGroup.message as string}
                </p>
              )}
            </div>
          </div> */}

          <div className="w-full">
            {/* Site ID */}
            <div className="flex flex-row gap-2 items-start">
              <div className="flex-1 flex flex-col gap-2">
                <Label htmlFor="siteId" className={cn(errors.siteId && "text-red-500")}>
                  {t("permanentservice.inputs.site.label")}
                </Label>
                <CustomCombobox
                  disabled={newSite}
                  options={customer?.sites.map((site) => ({
                    label: (
                      <>
                        {site.name}
                        <span className="text-xs text-gray-500"> - {appendSiteAddress(site)}</span>
                      </>
                    ),
                    value: site.id,
                    searchValue: `${site.name} ${appendSiteAddress(site)}`
                  })) ?? []}
                  value={watch("siteId")}
                  onChange={(value) => {
                    setValue("siteId", Number(value));
                    trigger("siteId");
                  }}
                  placeholder={t("permanentservice.inputs.site.selectplaceholder")}
                  error={!!errors.siteId}
                />
              </div>

              <Button
                type="button"
                variant="outline"
                disabled={newSite}
                onClick={() => {
                  setValue("siteId", 0);
                  setNewSite(true);
                }}
                className="mt-5.5"
              >
                {t("permanentservice.inputs.site.btnnewsite")}
              </Button>
            </div>
          </div>

          {newSite && <NewSiteComponent setNewSite={setNewSite} onAddSite={handleAddSite} onRemoveSite={handleRemoveSite} />}
        </div>
      </form>
      {/* End of Service form */}

      <div className="mt-4">
        {errors.shifts && (
          <p className="input-error-message -mb-2">
            {errors.shifts.message as string}
          </p>
        )}
        {/* Shifts Section */}
        <ShiftsComponent
          onAddShift={handleAddShift}
          onRemoveShift={handleRemoveShift}
          shiftsToDisplay={shiftsToDisplay}
          setShowRemoveShiftAlert={setShowRemoveShiftAlert}
          showRemoveShiftAlert={showRemoveShiftAlert}
          userCanEdit={true}
          onEditShift={handleEditShift}
        />
      </div>
    </>
  );
};

export default AddPermanentService;
